import { F0Dialog, f0FormField, useF0Form, useF0FormDefinition } from '@factorialco/f0-react'
import {
  EditProcurementTypeDocument,
  NewRequestPageDocument,
  ProcurementSettingsPageDocument,
  ProcurementTypeIdentityFragment,
} from 'generated/resources/graphql'
import { z } from 'zod'

import { FactorialF0Form } from 'modules/core/components/FactorialF0Form'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

interface Props {
  requestType: ProcurementTypeIdentityFragment
  onClose: () => void
}

const EditRequestTypeModal = ({ requestType, onClose }: Props) => {
  const { company } = useCurrentSession()

  const [editProcurementType] = useMutation(EditProcurementTypeDocument, {
    extractorFn: (data) => data.procurementMutations.updateProcurementType,
  })

  const { formRef, submit, isSubmitting, hasErrors, isDirty } = useF0Form()

  const schema = z.object({
    name: f0FormField(z.string().trim().min(1), {
      label: i18n.t('procurement.purchase_requests.settings.types.modals.detail.labels.name'),
    }),
    description: f0FormField(z.string().trim().max(75), {
      label: i18n.t(
        'procurement.purchase_requests.settings.types.modals.detail.labels.description'
      ),
      helpText: i18n.t(
        'procurement.purchase_requests.settings.types.modals.detail.descriptions.length'
      ),
    }),
  })

  const formDefinition = useF0FormDefinition({
    name: 'edit-request-type',
    schema,
    defaultValues: { name: requestType.name || '', description: requestType.description || '' },
    onSubmit: async ({ data }) => {
      const result = await editProcurementType({
        variables: {
          id: requestType.id,
          companyId: company.id,
          description: data.description,
          name: data.name,
        },
        refetchQueries: [ProcurementSettingsPageDocument, NewRequestPageDocument],
      })

      let fieldErrors: Record<string, string> = {}
      const handled = handleResult(result, (errors) => {
        fieldErrors = errors
      })

      if (!handled.success) {
        return { success: false, errors: fieldErrors }
      }

      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
      })

      onClose()
      return { success: true }
    },
    submitConfig: { type: 'default', hideSubmitButton: true },
  })

  return (
    <F0Dialog
      isOpen
      onClose={onClose}
      title={i18n.t('procurement.purchase_requests.settings.types.modals.edit.title')}
      description={i18n.t('procurement.purchase_requests.settings.types.modals.detail.description')}
      primaryAction={{
        label: i18n.t('procurement.purchase_requests.settings.types.modals.detail.buttons.save'),
        onClick: submit,
        loading: isSubmitting,
        disabled: hasErrors || !isDirty,
      }}
      secondaryAction={{
        label: i18n.t('procurement.purchase_requests.settings.types.modals.detail.buttons.cancel'),
        onClick: onClose,
      }}
    >
      <FactorialF0Form formRef={formRef} formDefinition={formDefinition} />
    </F0Dialog>
  )
}

export default EditRequestTypeModal
